
import { 
    FaDollarSign, 
    FaHashtag,  
    FaUser, 
    FaUserTag, 
    AppEnum,
    Header, 
    FaCalendar,
    OptionHelper,
    FaTasks,
    FaListOl,
    FaList,
    FaListUl,
    ActivityEnums,
    RecordMap
} from "@/Shared";

import { UserModel } from '@/Domains';

import { ActivityViewModel } from ".";

export type SelectOption = {
    value: any,
    label: any
}


/**
 * =====================================================
 * ActivityHeader
 * =====================================================
 *
 * Provides a structured definition of table headers for
 * displaying ActivityViewModel in a DataTable.
 *
 * Responsibilities:
 * - Define header properties: key, label, icon, type, sorting
 * - Provide filtering options when relevant (users, agents, enums)
 * - Centralize mapping between ActivityViewModel fields and UI headers
 * - Support dynamic construction of header set with build()
 *
 * Notes:
 * - Uses user lists and ActivityEnums to build filterable headers
 * - Avoids instance creation outside of static build method
 *
 * Author: Samano CASTRE
 * Date: August 3, 2025
 */
export class ActivityHeader {

    private constructor() { }

    get id() {
        return {
            key: 'id',
            icon: <FaHashtag />,
            label: 'ID',
            type: 'number',
            isSortable: true
        }
    }

     get startDate() {
        return {
            key: 'startDate',
            icon: <FaCalendar />,
            label: 'Début',
            type: 'date',
            isSortable: true
        }
    }

    get dueDate() {
        return {
            key: 'dueDate',
            icon: <FaCalendar />,
            label: 'Echéance',
            type: 'date',
            isSortable: true
        }
    }

    getStatus(statusEnum: AppEnum[]) {
        return {
            key: 'status',
            icon: <FaListUl  />,
            label: 'Statut',
            type: 'string',
            isSortable: true,
            filter: {
                label: "Statut",
                options: statusEnum,
                case: 'sensitive'
            }
        }
    }

    getCategory(categoryTypes: AppEnum[]) {
        return {
            key: 'taskCategory',
            icon: <FaList />,
            label: 'Catégorie',
            type: 'string',
            isSortable: true,
            filter: {
                label: "Catégorie",
                options: categoryTypes,
                case: 'sensitive'
            }
        }
    }

    getPriority(oprationTypes: AppEnum[]) {
        return {
            key: 'priority',
            icon: <FaListOl />,
            label: 'Priorité',
            type: 'string',
            isSortable: true,
            filter: {
                label: "Priorité",
                options: oprationTypes,
                case: 'sensitive'
            }
        }
    }


    get completionRate() {
        return {
            key: 'completionRate',
            icon: <FaUser />,
            label: '% Achevé',
            type: 'number',
            isSortable: true
        }
    }

    get description() {
        return {
            key: 'description',
            icon: <FaDollarSign />,
            label: 'Description',
            type: 'string',
            isSortable: true
        }
    }

    get taskLabel() {
        return {
            key: 'taskLabel',
            icon: <FaTasks />,
            label: 'Tâche',
            type: 'string',
            isSortable: true
        }
    }

    getInitiator(userMap: RecordMap<UserModel>) {
        return {
            key: 'initiatorUser',
            icon: <FaUserTag />,
            label: "Donneur d'ordre",
            type: 'string',
            isSortable: true,
            filter: {
                label: "Donneur d'ordre",
                options: OptionHelper.userOptions(userMap.toArray()),
                case: 'sensitive'
            }
        }
    }

    getUser(userMap: RecordMap<UserModel>) {
        return {
            key: 'assignedAgent',
            icon: <FaUserTag />,
            label: "Agent",
            type: 'string',
            isSortable: true,
            isHidden: false,
            filter: {
                label: "Agent",
                options: OptionHelper.userOptions(userMap.toArray()),
                case: 'sensitive'
            }
        }
    }

    /**
     * Builds a full header set for ActivityViewModel tables
     * @param userMap List of users to populate filters
     * @param viewEnums Enum values for categories, status, priority
     */
    static build(
        userMap: RecordMap<UserModel>,
        viewEnums : ActivityEnums

    ): Record<keyof ActivityViewModel, Header<ActivityViewModel>> {

        const header = new ActivityHeader();

        return {
            id: header.id,
            taskLabel : header.taskLabel,
            startDate : header.startDate,
            dueDate : header.dueDate,
            status : header.getStatus(viewEnums.ActivityStatusEnum),
            taskCategory : header.getCategory(viewEnums.TaskCategoryEnum),
            priority : header.getPriority(viewEnums.ActivityPriorityEnum),
            completionRate : header.completionRate,
            description : header.description,
            initiatorUser : header.getInitiator(userMap),
            assignedAgent : header.getUser(userMap)
        } as Record<keyof ActivityViewModel, Header<ActivityViewModel>>
    }
}